import React, { useState } from "react";
import s from "./AssignNumber.module.scss";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import clsx from "clsx";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import Select from "antd/es/select";
import { UserProfileIcon } from "../../../../../../global components/Icons/UserProfileIcon";
import { generateConfirmation } from "../../../../../../global components/DestructiveModal/DestruvtiveModal";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../libs/api/Endpoints";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { generatetoast } from "../../../../../../global components/Toast";
import { SuccessDataType } from "../../../../../../libs/api/types";
import { Button } from "antd";
import { PaginatedGetPendingRequestType } from "../../../../../../libs/Utils/Types/NumberManagement";
type InputType = {
  number: string;
  user: { value: string; label: string; name: string } | null;
};
type PropType = {
  closeDrawer: () => void;
  number: string;
  id?: string;
  mutate?: () => void;
};
function AssignNumber({ closeDrawer, number, id, mutate }: PropType) {
  const token = UseAuthToken();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage /*setCurrentPage*/] = useState(1);
  const [pageItems /*setPageItems*/] = useState(50);
  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    reset,
  } = useForm<InputType>({
    defaultValues: { number: number, user: null },
  });
  const { trigger: reassign, isMutating } = useSWRMutation(
    `api/admin/phone-numbers/requests/${watch("user.value")}/reassign`,
    swrMutationFetcher,
  );
  const { data: TableUsers, isLoading } = useSWR<
    PaginatedGetPendingRequestType,
    any,
    any
  >(
    token
      ? [
          "api/admin/phone-numbers/requests",
          token,
          {
            page: currentPage,
            limit: pageItems,
            search: searchValue,
            status: "pending",
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true, // Refetch when the window regains focus
      // Refetch every 10 seconds (10000 ms)
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );

  const onSubmit: SubmitHandler<InputType> = (data) => {
    handleReassign();
  };
  const handleSearch = (value: string) => {
    setSearchValue(value);
    // You can add your search logic here, e.g., fetching data based on the search term
  };
  const handleReassign = async () => {
    try {
      const fetcheArg: SwrMutationFetcherArgType = {
        token,
        requestOption: {
          method: "PATCH",
          body: JSON.stringify({ assignedNumberId: id }),
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await reassign(fetcheArg);
      closeDrawer();

      generatetoast("success", result.message);
      reset();
      mutate && mutate();
    } catch (error) {
      const e = parseClientError(error);
      mutate && mutate();

      generatetoast("error", e.message);
    }
  };

  return (
    <div className={s.wrapper}>
      <form className={s.form}>
        <div className={clsx(s.inputWrapper)}>
          <label htmlFor="phoneNo" className={clsx(s.inputLabel)}>
            Phone number
          </label>
          <Controller
            name="number"
            control={control}
            rules={{ required: "Please enter a phone number" }}
            render={({ field }) => (
              <PhoneInput
                {...field} // ensures that the value and onChange are linked
                // country={countryCode.toLowerCase()} // default country
                enableSearch={true} // Enable searching countries
                placeholder="Enter phone number"
                onChange={(value) => field.onChange(value)}
                value={field.value}
                disabled
                containerClass={s.phoneInputContainer} // Custom CSS for container
                dropdownClass={s.phoneInputDropdown} // Custom CSS for dropdown
                inputClass={clsx(
                  s.input,
                  { [s.inputError]: errors.number }, // Handle input error class
                  s.PhoneInput, // Additional styles for PhoneInput
                  s.input,
                )} // Input field class
                buttonClass={s.flagDropdown} // Dropdown for flag and country code
              />
            )}
          />

          {errors.number && (
            <span className={clsx(s.errorMessage)}>
              {errors.number.message}
            </span>
          )}
        </div>
        <div className={s.inputWrapper}>
          <label htmlFor="user" className={s.inputLabel}>
            Assign to
          </label>
          <Controller
            name="user"
            control={control}
            rules={{ required: "Please select a user" }}
            render={({ field }) => (
              <Select
                placeholder="Select a user"
                // labelInValue
                loading={isLoading}
                options={
                  TableUsers?.data?.length
                    ? TableUsers.data
                        .filter((item) => item.status === "pending")
                        .map((item) => ({
                          value: item.id,
                          label: (
                            <span className={s.userDrowDown}>
                              <UserProfileIcon className={s.icon} />
                              <p className={s.name}>
                                {item.user.firstName} {item.user.lastName}
                              </p>
                            </span>
                          ),
                          name: `${item.user.firstName} ${item.user.lastName}`,
                        }))
                    : []
                }
                notFoundContent={!isLoading && "No users found"}
                className={clsx(s.input, { [s.inputError]: errors.user })}
                popupClassName={s.selectPopup}
                showSearch
                filterOption={false}
                onSearch={handleSearch}
                value={field.value}
                onChange={(_, option: any) => {
                  field.onChange({
                    value: option.value,
                    label: option.label,
                    name: option.name,
                  });
                }}
              />
            )}
          />
          {errors.user && (
            <span className={clsx(s.errorMessage)}>{errors.user.message}</span>
          )}
        </div>
      </form>
      <div className={s.footer}>
        <Button
          loading={isMutating}
          disabled={!watch("number") || !watch("user")?.value}
          className={s.assignBtn}
          onClick={() =>
            generateConfirmation(
              "Re-assign number",
              `You are about to reassign this number ${watch("number")} to ${
                watch("user")?.name
              }.`,

              handleSubmit(onSubmit),
              isMutating,
              "reassign",
            )
          }
        >
          Reassign
        </Button>
      </div>
    </div>
  );
}

export default AssignNumber;
